import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import api from '../config/api';

function Settings() {
  const [settings, setSettings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    setting_key: '',
    setting_value: '',
    description: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data.data || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleOpen = (setting = null) => {
    if (setting) {
      setEditing(setting);
      setFormData(setting);
    } else {
      setEditing(null);
      setFormData({ setting_key: '', setting_value: '', description: '' });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({ setting_key: '', setting_value: '', description: '' });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (editing) {
        await api.put(`/settings/${editing.setting_key}`, {
          setting_value: formData.setting_value,
          description: formData.description,
        });
      } else {
        await api.post('/settings', formData);
      }
      handleClose();
      fetchSettings();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving setting');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">App Settings</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Setting
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settings.map((setting) => (
              <TableRow key={setting.id}>
                <TableCell>{setting.setting_key}</TableCell>
                <TableCell>{setting.setting_value}</TableCell>
                <TableCell>{setting.description || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(setting)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Setting' : 'Add Setting'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Setting Key"
            value={formData.setting_key}
            onChange={(e) => setFormData({ ...formData, setting_key: e.target.value })}
            margin="normal"
            required
            disabled={!!editing}
          />
          <TextField
            fullWidth
            label="Setting Value"
            value={formData.setting_value}
            onChange={(e) => setFormData({ ...formData, setting_value: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Settings;

